import React, { useState, useEffect } from "react";
import { TextField, MenuItem } from "@mui/material";
import "./components.css";
import { getLocalizedFieldLabel } from "../helpers/helpers";
import { leadAssignApiEndpoints } from "../constants";

const AddressAutoComplete = ({ formData, onSelectionChange, label, lang }) => {
  // Initialize address from formData
  const [address, setAddress] = useState(formData.address || "");
  const [suggestions, setSuggestions] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  // Initialize which fields are in editable mode (stored in localStorage)
  const [editableFields, setEditableFields] = useState(() => {
    const savedState = localStorage.getItem("editableFields");
    return savedState
      ? JSON.parse(savedState)
      : {
          address: false,
          city: false,
          state: false,
          county: false,
          zip: false,
        };
  });

  // Add focus tracking for each field
  const [focusedFields, setFocusedFields] = useState({
    address: false,
    city: false,
    state: false,
    county: false,
    zip: false,
  });

  // Track which fields have been interacted with (focused and blurred)
  const [interactedFields, setInteractedFields] = useState({
    address: false,
    city: false,
    state: false,
    county: false,
    zip: false,
  });

  // Initialize selectedCityData from formData
  // Only initialize if there's at least one non-empty value
  const [selectedCityData, setSelectedCityData] = useState(() => {
    const hasAnyValue = formData.city || formData.address || formData.state || formData.county || formData.zipCode;
    if (hasAnyValue) {
      return {
        address: formData.address || "",
        city: formData.city || "",
        state: formData.state || "",
        county: formData.county || "",
        zip: formData.zipCode || "",
      };
    }
    return null;
  });

  // Save editable fields state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("editableFields", JSON.stringify(editableFields));
  }, [editableFields]);

  // Sync selectedCityData with formData changes (e.g., when address fields are cleared)
  useEffect(() => {
    const hasAnyValue = formData.city || formData.address || formData.state || formData.county || formData.zipCode;
    if (!hasAnyValue) {
      // All address fields cleared, reset selectedCityData
      setSelectedCityData((prev) => {
        if (prev) {
          setAddress("");
          // Reset interacted fields when address is cleared
          setInteractedFields({
            address: false,
            city: false,
            state: false,
            county: false,
            zip: false,
          });
          return null;
        }
        return prev;
      });
    } else {
      // Update selectedCityData if formData has values
      const newData = {
        address: formData.address || "",
        city: formData.city || "",
        state: formData.state || "",
        county: formData.county || "",
        zip: formData.zipCode || "",
      };
      setSelectedCityData((prev) => {
        // Only update if there's a meaningful change
        if (!prev || 
            prev.address !== newData.address ||
            prev.city !== newData.city ||
            prev.state !== newData.state ||
            prev.county !== newData.county ||
            prev.zip !== newData.zip) {
          if (formData.address) {
            setAddress(formData.address);
          }
          return newData;
        }
        return prev;
      });
    }
  }, [formData.address, formData.city, formData.state, formData.county, formData.zipCode]);

  useEffect(() => {
    const trimmedAddress = (address || "").trim();
    
    // Clear suggestions if address is empty or too short
    if (!trimmedAddress || trimmedAddress.length < 3) {
      setSuggestions([]);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        const response = await fetch(
           leadAssignApiEndpoints.ADDRESS_ENDPOINT,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ input: trimmedAddress }),
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch suggestions");
        }

        const data = await response.json();
        setSuggestions(data?.predictions || []);
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.error("Error fetching address suggestions:", error);
        setSuggestions([]);
      }
    }, 400);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [address]);

  const handleSuggestionSelect = async (suggestion) => {
    // Mark address as editable
    setEditableFields((prev) => ({
      ...prev,
      address: true,
    }));

    // Mark address field as interacted when suggestion is selected
    setInteractedFields((prev) => ({
      ...prev,
      address: true,
    }));

    setAddress(suggestion?.description);
    setIsEditing(false); // Stop editing mode after selection

    try {
      const response = await fetch(
        leadAssignApiEndpoints.PLACES_ENDPOINT,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ place_id: suggestion?.place_id }),
        }
      );

      const data = await response.json();

      const selectedAddressDetails = {
        address: data?.address || suggestion?.description || "",
        city: data?.city || "",
        state: data?.state || "",
        county: data?.county || "",
        zip: data?.zip || "",
      };

      setSelectedCityData(selectedAddressDetails);
      onSelectionChange(selectedAddressDetails, suggestion?.description);
      setSuggestions([]);
    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  };

  const handleEdit = (field, value) => {
    // Mark the field as editable
    setEditableFields((prev) => ({
      ...prev,
      [field]: true,
    }));

    // Update the data
    const updatedData = {
      ...selectedCityData,
      [field]: value,
    };

    setSelectedCityData(updatedData);

    // Prepare data for parent component
    const dataForParent = {
      address: updatedData.address,
      city: updatedData.city,
      state: updatedData.state,
      county: updatedData.county,
      zip: updatedData.zip,
    };

    // If field is address, use it as the description
    const description = field === "address" ? value : address;

    // Notify parent component of changes
    onSelectionChange(dataForParent, description);
  };

  // Handle focus changes
  const handleFocus = (field) => {
    setFocusedFields((prev) => ({
      ...prev,
      [field]: true,
    }));

    // Enable editing mode when focusing on address field
    if (field === "address") {
      setIsEditing(true);
    }
  };

  const handleBlur = (field) => {
    setFocusedFields((prev) => ({
      ...prev,
      [field]: false,
    }));

    // Mark field as interacted with after blur
    setInteractedFields((prev) => ({
      ...prev,
      [field]: true,
    }));

    // We'll keep isEditing true until user makes a selection or clicks away
    if (field === "address") {
      // Small delay to allow suggestion click to happen first
      setTimeout(() => {
        if (suggestions.length > 0) {
          // Check if focus is still within the component
          const isFocusWithinComponent =
            document.activeElement &&
            document.activeElement.closest(".address-autocomplete-container");

          if (!isFocusWithinComponent) {
            setIsEditing(false);
            setSuggestions([]);
          }
        } else {
          setIsEditing(false);
        }
      }, 200);
    }
  };

  // Enable edit mode for a field
  const enableEditMode = (field) => {
    setEditableFields((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  // Check if a field should show error
  // Only show error if:
  // 1. selectedCityData exists (user has started entering address)
  // 2. Field has been interacted with (focused and blurred)
  // 3. Field is empty
  // 4. Field is not currently focused
  const shouldShowError = (field) => {
    if (!selectedCityData) return false;
    if (!interactedFields[field]) return false; // Don't show error if user hasn't interacted with field
    return !selectedCityData[field] && !focusedFields[field];
  };

  // Check if a field should be disabled
  const isFieldDisabled = (field) => {
    // If field has value from backend and hasn't been set to editable mode
    return (
      selectedCityData && selectedCityData[field] && !editableFields[field]
    );
  };

  // Custom styles applied directly to components
  const errorInputStyle = {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "red !important",
      borderWidth: "2px !important",
    },
    "& .MuiInputLabel-root": {
      color: "red !important",
    },
  };

  const focusedInputStyle = {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "green !important",
      borderWidth: "2px !important",
    },
    "& .MuiInputLabel-root": {
      color: "green !important",
    },
  };

  return (
    <div className="address-autocomplete-container" style={{ width: "100%" }}>
      <TextField
        label={getLocalizedFieldLabel("address", lang) || label}
        value={address}
        onChange={(e) => {
          const newValue = e.target.value;
          setAddress(newValue);
          setIsEditing(true);

          // Mark address as editable
          setEditableFields((prev) => ({
            ...prev,
            address: true,
          }));

          if (selectedCityData) {
            const updatedData = {
              ...selectedCityData,
              address: newValue,
            };
            setSelectedCityData(updatedData);
            onSelectionChange(updatedData, newValue);
          }
        }}
        className="flexAlignColumnClassName"
        InputLabelProps={{
          classes: {
            root: "outlinedInputLabel",
            focused: "focusedInputLabel",
          },
        }}
        style={{ marginBottom: "10px" }}
        error={shouldShowError("address")}
        sx={
          focusedFields.address
            ? focusedInputStyle
            : shouldShowError("address")
            ? errorInputStyle
            : {}
        }
        onFocus={() => handleFocus("address")}
        onBlur={() => handleBlur("address")}
        disabled={isFieldDisabled("address")}
        InputProps={{
          endAdornment: isFieldDisabled("address") ? (
            <button
              onClick={() => enableEditMode("address")}
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
                color: "#1976d2",
              }}
            >
              Edit
            </button>
          ) : null,
        }}
      />
      {suggestions.length > 0 && isEditing && (
        <div
          style={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            maxWidth: "100%", // Ensures the box doesn't exceed the screen width
            maxHeight: "50vh", // Limits height to half the viewport height
            overflowY: "auto", // Adds scroll for overflow content
            boxSizing: "border-box", // Prevents padding/border from expanding the box
            width: "100%", // Takes full available width
          }}
        >
          {suggestions.map((suggestion) => (
            <MenuItem
              key={suggestion.place_id}
              onClick={() => handleSuggestionSelect(suggestion)}
              style={{
                wordBreak: "break-word", // better behavior than break-all
                whiteSpace: "normal", // allow wrapping
                overflowWrap: "break-word", // fallback for older browsers
                width: "100%",
              }}
            >
              {suggestion.description}
            </MenuItem>
          ))}
        </div>
      )}
      {selectedCityData && (
        <div className="flexAlignColumnClassName">
          <TextField
            label={getLocalizedFieldLabel("city", lang)}
            value={selectedCityData.city}
            onChange={(e) => handleEdit("city", e.target.value)}
            className="textFieldInputComponent"
            margin="normal"
            InputLabelProps={{
              classes: {
                root: "outlinedInputLabel",
                focused: "focusedInputLabel",
              },
            }}
            disabled={isFieldDisabled("city")}
            error={shouldShowError("city")}
            sx={
              focusedFields.city
                ? focusedInputStyle
                : shouldShowError("city")
                ? errorInputStyle
                : {}
            }
            onFocus={() => handleFocus("city")}
            onBlur={() => handleBlur("city")}
            InputProps={{
              endAdornment: isFieldDisabled("city") ? (
                <button
                  onClick={() => enableEditMode("city")}
                  style={{
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    color: "#1976d2",
                  }}
                >
                  Edit
                </button>
              ) : null,
            }}
          />
          <TextField
            label={getLocalizedFieldLabel("state", lang)}
            value={selectedCityData.state}
            onChange={(e) => handleEdit("state", e.target.value)}
            className="textFieldInputComponent"
            margin="normal"
            InputLabelProps={{
              classes: {
                root: "outlinedInputLabel",
                focused: "focusedInputLabel",
              },
            }}
            disabled={isFieldDisabled("state")}
            error={shouldShowError("state")}
            sx={
              focusedFields.state
                ? focusedInputStyle
                : shouldShowError("state")
                ? errorInputStyle
                : {}
            }
            onFocus={() => handleFocus("state")}
            onBlur={() => handleBlur("state")}
            InputProps={{
              endAdornment: isFieldDisabled("state") ? (
                <button
                  onClick={() => enableEditMode("state")}
                  style={{
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    color: "#1976d2",
                  }}
                >
                  Edit
                </button>
              ) : null,
            }}
          />
          <TextField
            label={getLocalizedFieldLabel("county", lang)}
            value={selectedCityData.county}
            onChange={(e) => handleEdit("county", e.target.value)}
            className="textFieldInputComponent"
            margin="normal"
            InputLabelProps={{
              classes: {
                root: "outlinedInputLabel",
                focused: "focusedInputLabel",
              },
            }}
            disabled={isFieldDisabled("county")}
            error={shouldShowError("county")}
            sx={
              focusedFields.county
                ? focusedInputStyle
                : shouldShowError("county")
                ? errorInputStyle
                : {}
            }
            onFocus={() => handleFocus("county")}
            onBlur={() => handleBlur("county")}
            InputProps={{
              endAdornment: isFieldDisabled("county") ? (
                <button
                  onClick={() => enableEditMode("county")}
                  style={{
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    color: "#1976d2",
                  }}
                >
                  Edit
                </button>
              ) : null,
            }}
          />
          <TextField
            label={getLocalizedFieldLabel("zipCode", lang)}
            value={selectedCityData.zip}
            onChange={(e) => handleEdit("zip", e.target.value)}
            className="textFieldInputComponent"
            margin="normal"
            InputLabelProps={{
              classes: {
                root: "outlinedInputLabel",
                focused: "focusedInputLabel",
              },
            }}
            disabled={isFieldDisabled("zip")}
            error={shouldShowError("zip")}
            sx={
              focusedFields.zip
                ? focusedInputStyle
                : shouldShowError("zip")
                ? errorInputStyle
                : {}
            }
            onFocus={() => handleFocus("zip")}
            onBlur={() => handleBlur("zip")}
            InputProps={{
              endAdornment: isFieldDisabled("zip") ? (
                <button
                  onClick={() => enableEditMode("zip")}
                  style={{
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    color: "#1976d2",
                  }}
                >
                  Edit
                </button>
              ) : null,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AddressAutoComplete;
